import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import secureStorage from "../utils/secureStorage";
import { validateToken } from "../tokenValidation";

const ProfileCard = ({ profile, label }) => {
    const {
        firstName,
        middleName,
        lastName,
        gender,
        yearOfBirth,
        monthOfBirth,
        dayOfBirth,
        abhaNumber,
        ABHANumber,
        preferredAbhaAddress,
        mobile,
        mobileNumber,
        profilePhoto,
        address,
        pincode,
    } = profile;

    const formatGender = (g) => {
        if (g === "M") return "Male";
        if (g === "F") return "Female";
        return "Other";
    };

    const getFullName = () =>
        [firstName, middleName, lastName].filter(Boolean).join(" ").trim();

    const dobFormatted = dayOfBirth && monthOfBirth && yearOfBirth
        ? `${String(dayOfBirth).padStart(2, "0")} ${new Date(2000, parseInt(monthOfBirth) - 1).toLocaleString("en-IN", { month: "long" })} ${yearOfBirth}`
        : "N/A";

    return (
        <div className="border rounded-xl shadow-lg p-6 bg-white w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 text-center text-teal-600">{label}</h3>
            <div className="flex justify-center mb-4">
                {profilePhoto ? (
                    <img
                        src={`data:image/jpeg;base64,${profilePhoto}`}
                        alt="Profile"
                        className="w-32 h-32 rounded-full object-cover border-2 border-teal-600"
                    />
                ) : (
                    <div className="w-32 h-32 flex items-center justify-center rounded-full bg-gray-200 border-2 border-teal-600 text-center text-sm text-gray-500">
                        Profile image<br />not available
                    </div>
                )}
            </div>
            <p><strong>Name:</strong> {getFullName() || "N/A"}</p>
            <p><strong>Gender:</strong> {formatGender(gender)}</p>
            <p><strong>DOB:</strong> {dobFormatted}</p>
            <p><strong>ABHA Number:</strong> {abhaNumber || ABHANumber || "N/A"}</p>
            <p><strong>ABHA Address:</strong> {preferredAbhaAddress || "N/A"}</p>
            <p><strong>Mobile:</strong> {mobileNumber || mobile || "N/A"}</p>
            <p><strong>Address:</strong> {address || "N/A"}</p>
            <p><strong>Pin Code:</strong> {pincode || "N/A"}</p>
        </div>
    );
};

const ProfileComparison = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { abhaProfile, existingProfiles = [] } = location.state || {};
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(null);

    const getFullName = (profile) =>
        [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ").trim();

    const formatGender = (g) => {
        if (g === "M") return "Male";
        if (g === "F") return "Female";
        return "Other";
    };

    const formatDOB = (day, month, year) => {
        if (!day || !month || !year) return "N/A";
        return `${String(day).padStart(2, '0')} ${new Date(2000, parseInt(month) - 1).toLocaleString("en-IN", {
            month: "long"
        })} ${year}`;
    };

    useEffect(() => {
        validateToken(navigate);
    }, [navigate]);

    // Automatically skip list and show comparison if perfect match
    useEffect(() => {
        if (
            existingProfiles.length === 1 &&
            existingProfiles[0].preferredAbhaAddress &&
            abhaProfile?.preferredAbhaAddress &&
            existingProfiles[0].preferredAbhaAddress === abhaProfile.preferredAbhaAddress
        ) {
            setSelectedProfile(existingProfiles[0]);
        }
    }, [existingProfiles, abhaProfile]);


    const handleSelection = async (profile, type) => {
        setLoadingProfile(type);
        try {
            if (selectedProfile) {
                profile.patientId = selectedProfile.patientId;
            }

            const orgId = secureStorage.get("orgId");
            const response = await fetch(`${process.env.REACT_APP_API_URL}abdm/choose-profile?orgId=${orgId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-HIP-ID": secureStorage.get("clinic"),
                },
                body: JSON.stringify(profile),
            });

            const data = await response.json();
            if (response.ok && data.status === 200) {
                navigate("/patient-profile", {
                    state: { abhaAddress: profile?.preferredAbhaAddress || profile?.ABHANumber },
                });
            } else {
                toast.error(data.data || "Failed to save profile.");
            }
        } catch (err) {
            toast.error("Error occurred while saving profile.");
        } finally {
            setLoadingProfile(null);
        }
    };

    // Step 1: List view
    if (!selectedProfile) {
        return (
            <>
                <div className="min-h-screen bg-gray-50 p-6">
                    <h1 className="text-2xl font-bold text-center text-teal-700 mb-6">Select a Matching Profile</h1>
                    <div className="max-w-4xl mx-auto space-y-4">
                        {existingProfiles.length === 0 ? (
                            <p className="text-center text-gray-600">No existing profiles found.</p>
                        ) : (
                            <>
                                {existingProfiles.map((profile, index) => (
                                    <div
                                        key={profile.patientId || index}
                                        className="rounded-xl bg-white shadow-md hover:shadow-lg transition-transform hover:scale-[1.02] p-4 flex flex-col md:flex-row items-start gap-4"
                                    >
                                        {/* Profile Image */}
                                        {profile.profilePhoto ? (
                                            <img
                                                src={`data:image/jpeg;base64,${profile.profilePhoto}`}
                                                alt="Profile"
                                                className="w-20 h-20 rounded-full object-cover border-2 border-teal-600"
                                            />
                                        ) : (
                                            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gray-200 border-2 border-teal-600 text-center text-xs text-gray-500">
                                                No Image
                                            </div>
                                        )}

                                        {/* Profile Summary */}
                                        <div className="flex-1 w-full">
                                            <p><strong>Name :</strong> {getFullName(profile)}</p>
                                            <p><strong>Gender :</strong> {formatGender(profile.gender)}</p>
                                            <p><strong>DOB :</strong> {formatDOB(profile.dayOfBirth, profile.monthOfBirth, profile.yearOfBirth)}</p>
                                            <p><strong>Mobile :</strong> {profile.mobileNumber || profile.mobile || "N/A"}</p>
                                            {profile.abhaNumber && (
                                                <p><strong>ABHA Number :</strong> {profile.abhaNumber}</p>
                                            )}
                                            {profile.preferredAbhaAddress && (
                                                <p><strong>ABHA Address :</strong> {profile.preferredAbhaAddress}</p>
                                            )}
                                        </div>

                                        {/* Select Button */}
                                        <button
                                            className="mt-4 md:mt-0 bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 transition self-start"
                                            onClick={() => setSelectedProfile(profile)}
                                        >
                                            Select Profile
                                        </button>
                                    </div>
                                ))}

                                {/* Create New Profile Option */}
                                <div
                                    className={`cursor-pointer border border-dashed border-teal-600 rounded-xl bg-white shadow-md transition-transform p-6 text-center ${loadingProfile === "new" ? "opacity-60 cursor-wait" : "hover:shadow-lg hover:scale-[1.02]"}`}
                                    onClick={() => {
                                        if (!loadingProfile) {
                                            handleSelection(abhaProfile, "new");
                                        }
                                    }}
                                >
                                    <h3 className="text-lg font-semibold text-teal-700 mb-2">
                                        {loadingProfile === "new" ? "Creating Profile..." : "Create a New Profile"}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {loadingProfile === "new"
                                            ? "Please wait while we create your profile."
                                            : "No existing profiles match? You can proceed with creating a new profile based on the ABHA data."}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {/* Toast Notifications */}
                <ToastContainer />
            </>
        );
    }

    // Step 2: Comparison view
    return (
        <>
            <div className="min-h-screen bg-gray-50 p-6 flex flex-col items-center">
                <h1 className="text-2xl font-bold text-center text-teal-700 mb-6">Compare ABHA Profiles</h1>

                {!selectedProfile && (
                    <div className="mb-4 w-full max-w-5xl text-left">
                        <button
                            onClick={() => setSelectedProfile(null)}
                            className="text-teal-600 hover:underline"
                        >
                            ← Back to Profile List
                        </button>
                    </div>
                )}

                <div className="flex flex-col md:flex-row gap-10 w-full max-w-5xl items-stretch">
                    {[
                        ["Selected Existing Profile", selectedProfile, "existing"],
                        ["New ABHA Profile", abhaProfile, "new"],
                    ].map(([label, profile, type]) => (
                        <div
                            key={type}
                            className="flex-1 flex flex-col items-center justify-between bg-white rounded-xl shadow-lg p-6 min-h-[600px] max-w-md w-full"
                        >
                            <ProfileCard profile={profile} label={label} />
                            <button
                                onClick={() => handleSelection(profile, type)}
                                disabled={loadingProfile === type}
                                className={`mt-6 w-full bg-teal-600 text-white py-2 rounded-lg transition ${loadingProfile === type ? "opacity-60 cursor-not-allowed" : "hover:bg-teal-700"
                                    }`}
                            >
                                {loadingProfile === type ? "Accepting..." : `Accept ${label}`}
                            </button>
                        </div>
                    ))}
                </div>

            </div>

            <ToastContainer />
        </>
    );
};

export default ProfileComparison;
